import { useQuery } from '@tanstack/react-query';

import {
  BusinessUnitAssignedUserLoookup,
  BusinessUnitWorker,
  CompanyLookup,
  LegacyListPagedResult,
  ListPagedResult,
  PagedRequest,
  Result,
  TerminatedWorker,
} from '@xemplo/gp-types';
import { useHttpClient } from '@xemplo/http';
import { apiUrlBuilder } from '@xemplo/url-helper';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import { LookupsAPI } from './lookups-query.constants';

export const useGetBusinessUnitWorkersQuery = (props: {
  id: string | number | null;
  requestParams?: PagedRequest;
}) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams, id } = props;
  const url = apiUrlBuilder(
    LookupsAPI.workersByBusinessUnitId(id),
    applicationApiUrl,
    requestParams,
  );

  return useQuery<LegacyListPagedResult<BusinessUnitWorker>>({
    queryKey: [url, applicationApiUrl, requestParams],
    queryFn: () =>
      client
        .get<LegacyListPagedResult<BusinessUnitWorker>>(url)
        .then((resp) => resp.data),
    enabled: !!applicationApiUrl && !!id,
  });
};

export const useGetTerminatedBusinessUnitWorkersQuery = (props: {
  id: string | number | null;
  requestParams?: PagedRequest;
}) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams, id } = props;
  const url = apiUrlBuilder(
    LookupsAPI.terminatedWorkersByBusinessUnitId(id),
    applicationApiUrl,
    requestParams,
  );
  return useQuery<ListPagedResult<TerminatedWorker>>({
    queryKey: [url, applicationApiUrl, requestParams],
    queryFn: ({ signal }) =>
      client
        .get<ListPagedResult<TerminatedWorker>>(url, { signal })
        .then((resp) => resp.data),
    enabled: !!applicationApiUrl && !!id,
  });
};

export function useGetCompaniesQuery(props: { requestParams?: PagedRequest }) {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams } = props;
  const url = apiUrlBuilder(LookupsAPI.companies, applicationApiUrl, requestParams);

  return useQuery<ListPagedResult<CompanyLookup>>({
    queryKey: [url, applicationApiUrl, requestParams],
    queryFn: ({ signal }) =>
      client
        .get<ListPagedResult<CompanyLookup>>(url, { signal })
        .then((resp) => resp.data),
    enabled: !!applicationApiUrl,
  });
}

export function useGetBusinessUnitAssignedUsersQuery(props: {
  requestParams?: PagedRequest;
}) {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams } = props;
  const url = apiUrlBuilder(
    LookupsAPI.businessUnitAssignedUsers,
    applicationApiUrl,
    requestParams,
  );

  return useQuery<Result<BusinessUnitAssignedUserLoookup[]>>({
    queryKey: [url, applicationApiUrl, requestParams],
    queryFn: ({ signal }) =>
      client
        .get<Result<BusinessUnitAssignedUserLoookup[]>>(url, { signal })
        .then((resp) => resp.data),
    enabled: !!applicationApiUrl,
  });
}
